/**
 * Context React per la configurazione globale del tenant (DEC-029).
 *
 * Carica le chiavi di configurazione da GET /api/v1/configurazione
 * dopo l'autenticazione e le espone a tutti i componenti tramite useConfig().
 *
 * Fornisce un fallback statico prima della risposta API per evitare
 * flash di valori vuoti al primo render.
 */

import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

/** Mappa chiave → valore della configurazione tenant. */
type ConfigMap = Record<string, string>;

interface ConfigContextValue {
  /** Mappa completa chiave → valore. */
  config: ConfigMap;
  /** True mentre il fetch è in corso. */
  loading: boolean;
  /** Ricarica la configurazione dal server. */
  reload: () => Promise<void>;
}

/** Fallback usato prima della risposta API — valori identici al seed. */
const DEFAULT_CONFIG: ConfigMap = {
  locale: 'it-IT',
  valuta: 'EUR',
  simbolo_valuta: '€',
  formato_data: 'dd/MM/yyyy',
  formato_data_ora: 'dd/MM/yyyy HH:mm',
  formato_ora: 'HH:mm',
  fuso_orario: 'Europe/Rome',
  decimali_valuta: '2',
  decimali_quantita: '3',
  nome_azienda: '',
};

const ConfigContext = createContext<ConfigContextValue>({
  config: DEFAULT_CONFIG,
  loading: false,
  reload: async () => {},
});

const ConfigProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [config, setConfig] = useState<ConfigMap>(DEFAULT_CONFIG);
  const [loading, setLoading] = useState(false);

  const fetchConfig = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    setLoading(true);
    try {
      const res = await fetch('/api/v1/configurazione', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const data: Array<{ chiave: string; valore: string }> = await res.json();
      const map: ConfigMap = { ...DEFAULT_CONFIG };
      for (const entry of data) {
        map[entry.chiave] = entry.valore;
      }
      setConfig(map);
    } catch {
      /* Fallback silenzioso ai default — il sistema resta usabile. */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  return (
    <ConfigContext.Provider value={{ config, loading, reload: fetchConfig }}>
      {children}
    </ConfigContext.Provider>
  );
};

/** Hook per accedere alla configurazione globale del tenant. */
const useConfig = (): ConfigContextValue => useContext(ConfigContext);

export { ConfigProvider, useConfig };
